import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BaseCellComponent } from '../base-cell.component';
import { ViewManager } from 'src/app/board/pages/admin/admin-components/custom-views-config/view-builder/models/view-builder.models';
import { FileBrowserModalComponent, FileBrowserModalData } from '../../../file-browser-modal/file-browser-modal.component';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'bhub-file-reference-cell',
  standalone: false,
  templateUrl: './file-reference-cell.component.html',
  styleUrl: './file-reference-cell.component.scss'
})
export class FileReferenceCellComponent extends BaseCellComponent implements OnInit, OnDestroy {
  @Input() viewManager: ViewManager | null = null;
  @Output() contextDataChange = new EventEmitter<{[contextKey: string]: any}>();

  filePath: string = '';
  private destroy$ = new Subject<void>();

  get label(): string {
    return this.getConfigValue('fileRefLabel', '');
  }

  get contextObjectKey(): string {
    return this.cell?.dataBinding?.dataObjectKey || '';
  }

  get fieldName(): string {
    return this.cell?.dataBinding?.fieldName || '';
  }

  constructor(private dialog: MatDialog) {
    super();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected initializeCell(): void {
    this.loadFromContext();
  }

  protected onCellOrDataChange(): void {
    this.loadFromContext();
  }

  protected onCellUpdate(): void {
    this.loadFromContext();
  }

  protected onDataSet(data: any): void {}

  reloadData(): void {
    this.loadFromContext();
  }

  private loadFromContext(): void {
    if (!this.contextObjectKey || !this.fieldName || !this.viewManager?.contextData) {
      this.filePath = '';
      return;
    }

    const contextObj = this.viewManager.contextData[this.contextObjectKey];
    if (!contextObj || typeof contextObj !== 'object') {
      this.filePath = '';
      return;
    }

    const value = Array.isArray(contextObj) && contextObj.length > 0
      ? contextObj[0][this.fieldName]
      : contextObj[this.fieldName];

    this.filePath = value || '';
  }

  openFileBrowser(): void {
    if (this.isEditMode) return;

    const dialogData: FileBrowserModalData = {
      title: 'Select File',
      allowedStorageAreas: ['user', 'organization', 'admin']
    };

    const dialogRef = this.dialog.open(FileBrowserModalComponent, {
      width: '800px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      data: dialogData
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: string | null) => {
        if (result) {
          this.filePath = result;
          this.storeToContext(result);
        }
      });
  }

  clearFile(): void {
    this.filePath = '';
    this.storeToContext('');
  }

  private storeToContext(path: string): void {
    if (!this.contextObjectKey || !this.fieldName || !this.viewManager?.contextData) return;

    const contextObj = this.viewManager.contextData[this.contextObjectKey];
    if (contextObj && typeof contextObj === 'object' && !Array.isArray(contextObj)) {
      contextObj[this.fieldName] = path;
      this.contextDataChange.emit({ [this.contextObjectKey]: { ...contextObj } });
    }
  }

  getDisplayPath(): string {
    if (!this.filePath) return 'No file selected';
    // Show just the filename portion
    const parts = this.filePath.split('/');
    return parts[parts.length - 1] || this.filePath;
  }
}
